import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { saveAs } from "file-saver";
import { ReactComponent as ExcelIcon } from "../../assets/svg/Excel.svg";
import { selectUserInformations } from "../../features/authSlice";
import { deleteUser, selectUserDeleteStatus, selectUserDeleteError } from "../../features/users/userSlice";
import { FiEdit, FiRefreshCw, FiShield, FiShieldOff, FiAlertCircle } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { hasPermission, showToast } from "../../utils/utils";
import { formatPhoneNumberForDisplay } from "../../utils/phoneUtils";
import Pagination from "../reusable/Pagination";
import ActionsDropdown from "../reusable/ActionsDropdown";
import Loader from "../Live/Loader";
import { FaSortDown, FaSortUp } from "react-icons/fa";
import { API } from "../../shared/api/Api";

/**
 * UserTable Component
 * Refactored to use navigation instead of modals
 * - Create: Navigate to /dashboard/users/create
 * - Edit: Navigate to /dashboard/users/:id/edit
 * - Preview: Navigate to /dashboard/users/:id
 * - Delete: Confirmation dialog (handled in component)
 */
const UserTable = ({
  users,
  currentPage,
  totalPages,
  onSearchChange,
  onPageChange,
  loader,
  setReFetchData,
  onSort,
  currentOrderBy,
  currentOrderDir,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authUser = useSelector(selectUserInformations);
  const deleteStatus = useSelector(selectUserDeleteStatus);
  const deleteError = useSelector(selectUserDeleteError);

  // Blacklist state
  const [blacklistLoading, setBlacklistLoading] = useState(null);
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [blacklistReason, setBlacklistReason] = useState("");

  // Handle delete with confirmation
  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user? There is no going back!!")) {
      const result = await dispatch(deleteUser({ token: authUser.token, userId }));

      if (deleteUser.fulfilled.match(result)) {
        showToast('Success', 'User deleted successfully', 'success');
        setReFetchData(true);
      } else if (deleteUser.rejected.match(result)) {
        showToast('Error', result.payload || 'Failed to delete user', 'error');
      }
    }
  };

  // Handle blacklist user
  const handleBlacklistUser = async () => {
    if (!selectedUser) return;

    setBlacklistLoading(selectedUser.uuid);
    try {
      const { data } = await axios.post(
        API.Users.app_blacklist_add,
        {
          uuid: selectedUser.uuid,
          reason: blacklistReason || "Violated platform policy"
        },
        { headers: { Authorization: `Bearer ${authUser.token}` } }
      );

      if (data.status === 200 && data.errorCode === 0) {
        showToast("Success", "User has been blocked successfully", "success");
        setReFetchData(true);
      } else {
        showToast("Error", data.message || "Failed to block user", "error");
      }
    } catch (error) {
      showToast("Error", error.response?.data?.message || "Failed to block user", "error");
    } finally {
      setBlacklistLoading(null);
      setShowBlacklistModal(false);
      setSelectedUser(null);
      setBlacklistReason("");
    }
  };

  // Handle unblock user
  const handleUnblockUser = async (userUuid) => {
    if (!window.confirm("Are you sure you want to unblock this user?")) return;

    setBlacklistLoading(userUuid);
    try {
      const { data } = await axios.delete(
        `${API.Users.app_blacklist_delete}/${userUuid}`,
        { headers: { Authorization: `Bearer ${authUser.token}` } }
      );

      if (data.status === 200 && data.errorCode === 0) {
        showToast("Success", "User has been unblocked successfully", "success");
        setReFetchData(true);
      } else {
        showToast("Error", data.message || "Failed to unblock user", "error");
      }
    } catch (error) {
      showToast("Error", error.response?.data?.message || "Failed to unblock user", "error");
    } finally {
      setBlacklistLoading(null);
    }
  };

  const exportToExcel = async () => {
    try {
      const response = await axios.get(API.Users.app_users_export_to_excel, {
        responseType: "blob",
        headers: {
          Authorization: `Bearer ${authUser.token}`,
        },
      });
      let filename;
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.indexOf("filename=") !== -1) {
        filename = disposition.split("filename=")[1].trim().replace(/['"]/g, "");
      } else {
        const now = new Date();
        const pad = (n) => n.toString().padStart(2, "0");
        const formatted = `${now.getFullYear()}_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}_${pad(now.getMinutes())}_${pad(now.getSeconds())}`;
        filename = `users_${formatted}.xlsx`;
      }
      saveAs(response.data, filename);
      showToast("Success", "Users exported successfully", "success");
    } catch (error) {
      showToast(
        "Export Failed",
        error?.response?.data?.message || "Could not export users. Please try again.",
        "error"
      );
    }
  };

  // Open blacklist modal
  const openBlacklistModal = (user) => {
    setSelectedUser(user);
    setBlacklistReason("");
    setShowBlacklistModal(true);
  };

  // Handle delete errors
  useEffect(() => {
    if (deleteStatus === 'failed' && deleteError) {
      showToast('Error', deleteError, 'error');
    }
  }, [deleteStatus, deleteError]);

  // Define columns with sortable property
  const columns = [
    { id: "name", label: "Name", sortable: true },
    { id: "email", label: "Email", sortable: true },
    { id: "username", label: "Username", sortable: true },
    { id: "phone_number", label: "Phone Number", sortable: true },
    { id: "status", label: "Status", sortable: false },
    { id: "created_at", label: "Created At", sortable: true },
  ];

  // Handle sorting when a column header is clicked
  const handleSortClick = (columnId) => {
    if (currentOrderBy === columnId) {
      const newDirection = currentOrderDir === "ASC" ? "DESC" : "ASC";
      onSort(columnId, newDirection);
    } else {
      onSort(columnId, "ASC");
    }
  };

  // Render sort indicators
  const renderSortIndicator = (columnId) => {
    const isActive = currentOrderBy === columnId;

    return (
      <div className="inline-flex flex-col ml-1">
        <FaSortUp
          className={`${isActive && currentOrderDir === "ASC"
              ? "text-primary"
              : "text-gray-400"
            } -mb-1`}
          size={12}
        />
        <FaSortDown
          className={`${isActive && currentOrderDir === "DESC"
              ? "text-primary"
              : "text-gray-400"
            } -mt-1`}
          size={12}
        />
      </div>
    );
  };

  const getDropdownActions = (user) => {
    const actions = [];

    if (hasPermission(authUser, "view_user")) {
      actions.push({
        icon: (
          <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ),
        label: "Preview",
        onClick: () => navigate(`/dashboard/users/${user.uuid}`),
      });
    }

    if (hasPermission(authUser, "update_user")) {
      actions.push({
        icon: <FiEdit className="w-4 h-4" />,
        label: "Edit",
        onClick: () => navigate(`/dashboard/users/${user.uuid}/edit`),
      });
    }

    // Blacklist/Unblock actions
    if (hasPermission(authUser, "update_user")) {
      if (user.blackListed) {
        actions.push({
          icon: <FiShield className="w-4 h-4 text-green-500" />,
          label: "Unblock User",
          onClick: () => handleUnblockUser(user.uuid),
        });
      } else {
        actions.push({
          icon: <FiShieldOff className="w-4 h-4 text-red-500" />,
          label: "Block User",
          onClick: () => openBlacklistModal(user),
        });
      }
    }

    if (hasPermission(authUser, "delete_user")) {
      actions.push({
        icon: <MdDeleteOutline className="w-4 h-4" />,
        label: "Delete",
        onClick: () => handleDelete(user.uuid),
      });
    }

    return actions;
  };

  return (
    <div className="w-full mx-auto mt-8 px-2 sm:px-4 lg:px-0">
      <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-4 sm:p-6 lg:p-8 relative">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-anek-odia text-2xl sm:text-3xl font-semibold text-gray-900">
              Users
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage platform users, their status, and access control.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {hasPermission(authUser, "update_user") && (
              <button
                type="button"
                onClick={() => navigate('/dashboard/users/blacklist')}
                className="inline-flex items-center justify-center gap-2 text-white bg-red-500 hover:bg-red-600 rounded-full px-4 h-10 sm:h-11 text-xs sm:text-sm font-medium transition-colors"
                title="View Blocked Users"
              >
                <FiShieldOff className="w-4 h-4" />
                <span className="hidden sm:inline">Blocked Users</span>
              </button>
            )}
            {hasPermission(authUser, "view_user") && (
              <button
                type="button"
                onClick={exportToExcel}
                className="inline-flex items-center justify-center gap-2 text-white bg-emerald-500 hover:bg-emerald-600 rounded-full px-3 h-10 sm:h-11 text-xs sm:text-sm font-medium transition-colors"
                title="Export to Excel"
              >
                <ExcelIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </button>
            )}
            {hasPermission(authUser, "create_user") && (
              <button
                type="button"
                onClick={() => navigate('/dashboard/users/create')}
                className="inline-flex items-center justify-center gap-2 text-white bg-primary rounded-full px-3 sm:px-4 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-primary-hover transition-colors"
                title="Add User"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                  <svg
                    className="w-3.5 h-3.5"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 18 18"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 1v16M1 9h16"
                    />
                  </svg>
                </span>
                <span className="hidden sm:inline">New User</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setReFetchData(true)}
              className="inline-flex items-center justify-center gap-2 text-gray-700 bg-white border border-gray-200 rounded-full px-3 h-10 sm:h-11 text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors"
              title="Refresh Data"
            >
              <FiRefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Header: Search */}
        <div className="mt-5 flex justify-end items-center border-t border-gray-100 pt-4">
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              placeholder="Search users..."
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-full rounded-full border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary transition"
            />
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M11 5a6 6 0 100 12 6 6 0 000-12z"
                />
              </svg>
            </span>
          </div>
        </div>

        {/* Table Container with horizontal scroll on small screens */}
        <div className="mt-6 rounded-2xl border border-gray-100 bg-white overflow-hidden">
          <div className="w-full overflow-x-auto">
            <div className="min-w-[900px]">
              {loader && (
                <div className="flex justify-center items-center py-6">
                  <Loader />
                </div>
              )}

              <table className="w-full table-auto">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.id}
                        className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide ${currentOrderBy === column.id
                            ? "text-primary"
                            : "text-gray-500"
                          } border-b border-gray-100 ${column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                          }`}
                        onClick={
                          column.sortable
                            ? () => handleSortClick(column.id)
                            : undefined
                        }
                      >
                        <div className="flex items-center gap-1">
                          {column.label}
                          {column.sortable && renderSortIndicator(column.id)}
                        </div>
                      </th>
                    ))}

                    {(hasPermission(authUser, "update_user") ||
                      hasPermission(authUser, "delete_user") ||
                      hasPermission(authUser, "view_user")) && (
                        <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                          Actions
                        </th>
                      )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.length === 0 && !loader ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="px-4 sm:px-6 py-10 text-center text-gray-500"
                      >
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user, index) => (
                      <tr
                        key={index}
                        className={`hover:bg-gray-50 transition-colors cursor-auto text-sm ${user.blackListed ? "bg-red-50/60" : ""
                          }`}
                      >
                        <td className="px-4 sm:px-6 py-4 flex items-center space-x-4 text-sm sm:text-[15px] font-[400] text-left text-gray-700">
                          <div className="relative">
                            <img
                              src={user.image_url}
                              alt={user.name}
                              className={`w-10 h-10 sm:w-[39px] sm:h-[39px] rounded-full object-fill ${user.blackListed ? "opacity-60 grayscale" : ""
                                }`}
                            />
                            {user.blackListed && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                <FiShieldOff className="w-2.5 h-2.5 text-white" />
                              </div>
                            )}
                          </div>
                          <span className={user.blackListed ? "text-gray-500" : ""}>{user.name}</span>
                        </td>
                        <td className="px-4 sm:px-6 py-4 text-gray-700">{user.email}</td>
                        <td className="px-4 sm:px-6 py-4 text-gray-700">{user.username || ""}</td>
                        <td className="px-4 sm:px-6 py-4 text-gray-700">
                          {user.phone_number ? formatPhoneNumberForDisplay(user.phone_number) : ""}
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          {blacklistLoading === user.uuid ? (
                            <div className="flex justify-center">
                              <div className="w-5 h-5 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
                            </div>
                          ) : user.blackListed ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <FiShieldOff className="w-3 h-3" />
                              Blocked
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <FiShield className="w-3 h-3" />
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-4 sm:px-6 py-4 text-gray-700">{user.created_at}</td>
                        {(hasPermission(authUser, "update_user") ||
                          hasPermission(authUser, "delete_user") ||
                          hasPermission(authUser, "view_user")) && (
                            <td className="px-4 sm:px-6 py-4 text-right">
                              <ActionsDropdown actions={getDropdownActions(user)} />
                            </td>
                          )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mt-4">
          <p className="hidden sm:block text-xs text-gray-500">
            Showing page <span className="font-semibold">{currentPage}</span> of{" "}
            <span className="font-semibold">{totalPages}</span>
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>

        {/* Blacklist Modal */}
        {showBlacklistModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowBlacklistModal(false)}
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4 z-10"
            >
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <FiAlertCircle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Block User
                  </h3>
                  <p className="text-sm text-gray-500">
                    This will prevent the user from accessing the platform
                  </p>
                </div>
              </div>

              {/* User Info */}
              {selectedUser && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
                  <img
                    src={selectedUser.image_url}
                    alt={selectedUser.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-medium text-gray-900">{selectedUser.name}</p>
                    <p className="text-sm text-gray-500">{selectedUser.email}</p>
                  </div>
                </div>
              )}

              {/* Reason Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for blocking (optional)
                </label>
                <textarea
                  value={blacklistReason}
                  onChange={(e) => setBlacklistReason(e.target.value)}
                  placeholder="Enter the reason for blocking this user..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white resize-none"
                  rows={3}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowBlacklistModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBlacklistUser}
                  disabled={blacklistLoading}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {blacklistLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Blocking...
                    </>
                  ) : (
                    <>
                      <FiShieldOff className="w-4 h-4" />
                      Block User
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserTable;
